import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ShieldCheck, Lock, ChevronDown } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import client from "../lib/api";

const money = (n, c) => `${c ? c + " " : ""}${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const COLORS = ["#0F62FE", "#24A148", "#F1C21B", "#8A3FFC", "#08BDBA", "#FF832B", "#DA1E28", "#4589FF", "#D12771", "#007D79"];

export default function Portal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [openId, setOpenId] = useState(null);
  const [allocMode, setAllocMode] = useState("category");

  useEffect(() => {
    client.get(`/portal/view/${token}`).then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "This portal link is not active."));
  }, [token]);

  if (err) return (
    <div className="flex min-h-screen items-center justify-center bg-[#0b0f17] p-6 text-center text-white" data-testid="portal-error">
      <div><Lock size={40} className="mx-auto mb-3 opacity-60" /><p className="text-lg font-semibold">{err}</p>
        <p className="mt-1 text-sm opacity-60">Ask the family office for an active link.</p></div>
    </div>
  );
  if (!data) return <div className="flex min-h-screen items-center justify-center bg-[#0b0f17] text-white/60">Loading…</div>;

  const cur = data.base_currency;
  const rows = [...data.entities];
  if (data.unassigned?.net_value) rows.push({ name: "Unassigned holdings", type: "—", effective_pct: 100, net_value: data.unassigned.net_value, attributable_value: data.unassigned.net_value, share_pct: data.unassigned.share_pct, holding_count: data.unassigned.count, __unassigned: true });

  const alloc = (allocMode === "entity" ? data.allocation?.by_entity : data.allocation?.by_category) || [];
  const primaryCustom = data.custom_primary && data.logo;

  return (
    <div className="min-h-screen bg-[#0b0f17] px-4 py-10 text-white" data-testid="portal-page">
      <div className="mx-auto max-w-4xl">
        {/* header: primary logo (left) · household (center) · secondary logo/badge (right) — per support tier */}
        <div className="grid grid-cols-3 items-center gap-2">
          <div className="flex items-center gap-2">
            {primaryCustom
              ? <img src={data.logo} alt="Family office" className="h-9 w-auto rounded" data-testid="portal-logo-custom" onError={(e) => { e.currentTarget.style.display = "none"; }} />
              : (data.show_fopfo ? <img src="/brand-logo.png" alt={data.suite} className="h-8 w-auto opacity-90" data-testid="portal-logo-fopfo" /> : null)}
          </div>
          <div className="text-center text-sm font-semibold uppercase tracking-widest text-white/80" data-testid="portal-household">{data.household}</div>
          <div className="flex items-center justify-end gap-2">
            {!primaryCustom && data.logo
              ? <img src={data.logo} alt="Family office" className="h-8 w-auto rounded" data-testid="portal-logo-custom" onError={(e) => { e.currentTarget.style.display = "none"; }} />
              : <div className="flex items-center gap-1 rounded-full border border-white/15 px-3 py-1 text-xs text-white/70" data-testid="portal-readonly"><ShieldCheck size={13} className="text-[#24A148]" />Read-only</div>}
          </div>
        </div>

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="mt-8">
          <div className="text-sm text-white/60">Consolidated attributable net worth</div>
          <div className="mt-2 text-5xl font-bold text-white" data-testid="portal-total">{money(data.total_attributable, cur)}</div>
          <div className="mt-1 text-xs text-white/40">{data.entity_count} entities · governed net (assets − liabilities) · as of {String(data.generated).slice(0, 10)}</div>
        </motion.div>

        {/* asset-allocation donut with Category/Entity toggle */}
        {alloc.length ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }}
            className="mt-8 rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur" data-testid="portal-allocation">
            <div className="mb-2 flex items-center justify-between">
              <div className="text-sm font-semibold text-white/80">Asset allocation</div>
              <div className="inline-flex overflow-hidden rounded-full border border-white/15 text-xs">
                <button onClick={() => setAllocMode("category")} className={`px-3 py-1 ${allocMode === "category" ? "bg-[#08BDBA] text-black" : "text-white/70"}`} data-testid="alloc-toggle-category">By category</button>
                <button onClick={() => setAllocMode("entity")} className={`px-3 py-1 ${allocMode === "entity" ? "bg-[#08BDBA] text-black" : "text-white/70"}`} data-testid="alloc-toggle-entity">By entity</button>
              </div>
            </div>
            <div className="flex flex-col items-center gap-4 sm:flex-row">
              <div className="h-56 w-full sm:w-1/2" data-testid="portal-donut">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={alloc} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={2} stroke="none">
                      {alloc.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => money(v, cur)} contentStyle={{ background: "#111827", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 8, color: "#fff" }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="w-full space-y-1.5 sm:w-1/2" data-testid="portal-alloc-legend">
                {alloc.slice(0, 8).map((a, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                    <span className="flex-1 truncate text-white/80">{a.name}</span>
                    <span className="tabular-nums text-white/60">{a.pct}%</span>
                    <span className="w-24 text-end tabular-nums text-white/50">{money(a.value, cur)}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        ) : null}

        <div className="mt-8 space-y-2" data-testid="portal-entities">
          {rows.map((e, i) => {
            const id = e.__unassigned ? "__unassigned__" : (e.name + i);
            const open = openId === id;
            const hasDetail = !e.__unassigned;
            return (
              <motion.div key={id} initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 * i }}
                className="overflow-hidden rounded-xl border border-white/10 bg-white/5 backdrop-blur">
                <button type="button" disabled={!hasDetail} onClick={() => setOpenId(open ? null : id)}
                  className="flex w-full items-center justify-between p-4 text-start disabled:cursor-default" data-testid={`portal-entity-${i}`}>
                  <div>
                    <div className="flex items-center gap-1.5 font-semibold">
                      {e.name}
                      {hasDetail ? <ChevronDown size={14} className={`opacity-50 transition-transform ${open ? "rotate-180" : ""}`} /> : null}
                    </div>
                    <div className="text-xs text-white/50">{e.type}{e.effective_pct != null && !e.__unassigned ? ` · ${e.effective_pct}% effective ownership` : ""}{e.holding_count ? ` · ${e.holding_count} holding${e.holding_count === 1 ? "" : "s"}` : ""}</div>
                  </div>
                  <div className="text-end">
                    <div className="text-lg font-semibold tabular-nums">{money(e.attributable_value, cur)}</div>
                    <div className="text-xs text-white/50">{e.share_pct}%</div>
                  </div>
                </button>
                <div className="px-4 pb-3">
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/10">
                    <div className="h-full rounded-full" style={{ width: `${Math.min(e.share_pct, 100)}%`, background: COLORS[i % COLORS.length] }} />
                  </div>
                </div>
                <AnimatePresence initial={false}>
                  {open && hasDetail && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25 }} className="border-t border-white/10 bg-black/20" data-testid={`portal-entity-detail-${i}`}>
                      <div className="grid grid-cols-2 gap-x-6 gap-y-2 px-4 py-3 text-sm sm:grid-cols-4">
                        <Stat label="Direct assets" value={money(e.direct_value, cur)} />
                        <Stat label="Less liabilities" value={e.liabilities ? `−${money(e.liabilities, cur)}` : money(0, cur)} tone="#FF8389" />
                        <Stat label="Net value" value={money(e.net_value, cur)} />
                        <Stat label="Effective %" value={`${e.effective_pct}%`} />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>

        <p className="mt-8 text-center text-xs text-white/30">
          Shared securely by {data.household}. Figures are a governed net-worth summary, not audited financial statements
          or investment advice. Do not forward this link.
        </p>
      </div>
    </div>
  );
}

const Stat = ({ label, value, tone }) => (
  <div>
    <div className="text-[11px] uppercase tracking-wide text-white/40">{label}</div>
    <div className="mt-0.5 font-semibold tabular-nums" style={tone ? { color: tone } : undefined}>{value}</div>
  </div>
);
